import React, { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Box, Button, Link, Typography } from "@mui/material";
import {
  ANDROID_PACKAGE,
  APP_SCHEME,
  APP_STORE_URL,
  LANDING_URL,
  PLAY_STORE_URL,
  WEB_HOST,
} from "../constants";

// Only the public paths linked from outside (the events bulletin email) are
// gated; the rest of the web app is the DJ panel and stays reachable.
const GATED_PATHS = ["/upcoming-events"];

function getMobileOS() {
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "android";
  if (
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ reports itself as a Mac
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  )
    return "ios";
  return null;
}

export default function MobileAppGate({ children }) {
  const location = useLocation();
  const os = useRef(getMobileOS()).current;
  const fallbackTimer = useRef();

  const gated = GATED_PATHS.includes(location.pathname.toLowerCase());
  const active = !!os && gated;
  // Desktop visitors following bulletin links should land on the marketing
  // site, not the DJ panel. Only on the production host so localhost and
  // demo keep working.
  const redirectToLanding =
    !os && gated && window.location.hostname === WEB_HOST;

  useEffect(() => {
    if (redirectToLanding) window.location.replace(LANDING_URL);
  }, [redirectToLanding]);

  const openApp = useCallback(() => {
    const path = window.location.pathname + window.location.search;
    if (os === "android") {
      // Chrome opens the app if installed, otherwise follows the fallback
      // URL to the Play Store — no timers needed.
      window.location.href = `intent://${WEB_HOST}${path}#Intent;scheme=https;package=${ANDROID_PACKAGE};S.browser_fallback_url=${encodeURIComponent(
        PLAY_STORE_URL
      )};end`;
    } else if (os === "ios") {
      clearTimeout(fallbackTimer.current);
      fallbackTimer.current = setTimeout(() => {
        // Still visible means the scheme did not open the app.
        if (!document.hidden) window.location.href = APP_STORE_URL;
      }, 2500);
      window.location.href = `${APP_SCHEME}://${path.replace(/^\//, "")}`;
    }
  }, [os]);

  useEffect(() => {
    if (!active) return;
    openApp();
    const cancel = () => clearTimeout(fallbackTimer.current);
    window.addEventListener("pagehide", cancel);
    return () => {
      cancel();
      window.removeEventListener("pagehide", cancel);
    };
  }, [active, openApp]);

  if (redirectToLanding) return null;
  if (!active) return children;

  const storeUrl = os === "ios" ? APP_STORE_URL : PLAY_STORE_URL;
  const badge =
    os === "ios" ? "/appstoreButton.png" : "/playstoreButton.png";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        px: 3,
        textAlign: "center",
      }}
    >
      <img
        src={`${process.env.PUBLIC_URL}/logo.png`}
        alt="VIBY"
        style={{ width: 120 }}
      />
      <Typography variant="h5" sx={{ color: "#ede7f6" }}>
        VIBY is better in the app
      </Typography>
      <Button
        variant="contained"
        onClick={openApp}
        sx={{
          bgcolor: "#d21b89",
          "&:hover": { bgcolor: "#c9166c" },
          color: "#fff",
          width: 200,
          py: 1.5,
        }}
      >
        Open the app
      </Button>
      <a href={storeUrl}>
        <img
          src={`${process.env.PUBLIC_URL}${badge}`}
          alt={
            os === "ios" ? "Download on the App Store" : "Get it on Google Play"
          }
          style={{ width: 200 }}
        />
      </a>
      <Link
        href={LANDING_URL}
        underline="always"
        sx={{ color: "#d8c8f4", textDecorationColor: "#d8c8f4" }}
      >
        Continue to website
      </Link>
    </Box>
  );
}
